import { createContext, FC, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import {
  QuestionnaireResponse,
  QuestionnaireResponseItem,
  QuestionnaireResponseItemAnswer,
  Reference,
  ServiceRequest,
  Task
} from '@medplum/fhirtypes'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { useMedplum } from '@medplum/react'
import {
  extractAnswers,
  getAnswerValuesFromItems,
  getPriorAuthRequestPrimaryServiceTypeName,
  getShouldFinalizeAssessment,
  updateObjectByLinkId
} from 'components/Assessments/helpers'
import { removeNullValues } from 'utils/helpers'
import { rebuildResponseFromFlat } from 'components/Patients/Assessments/CreateAssessment/helpers'
import { SocAssessmentQuestionnaireItem } from 'components/Assessments/SocAssessment/types/questionnaire'
import { AssessmentStatus, COMPLETION_STATUSES } from 'fhir/QuestionnaireResponse/constants'
import { AssessmentType } from 'components/Clinical/Assessments/constants'
import { ServiceTypeName } from 'fhir/HealthcareService/constants'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { QueryKeys } from 'utils/query.constants'
import { LOINC_CODE_PRIOR_AUTH_REQUEST } from 'fhir/ServiceRequest/categories'
import { OrderType } from 'components/Patients/Orders/CreateOrder/constants'
import { FuaAssessmentQuestionnaireItem } from 'components/Assessments/FuaAssessment/types/questionnaire'
import { getFlowFromReasonForDocumentation } from 'components/Assessments/utils/getFuaFlows'
import { BaseQuestionnaireStructureFragment } from 'generated/graphql'
import { QuestionnaireStatus } from 'fhir/Questionnaire/constants'
import { BASE_API_URL } from 'utils/constants'
import { HttpClient } from 'utils/httpClient'
import { useStore } from 'store'
import PatientContext from './PatientContext'
import AccessPolicyContext from './AccessPolicyContext'

export interface LinkIdWithItem {
  linkId: string
  item: QuestionnaireResponseItem
}

export const enum AcutePeriodFlow {
  CHANGE_IN_CONDITION = 'change-in-condition',
  EXISTING_ACUTE_PERIOD = 'existing-acute-period',
  REAUTHORIZATION = 'reauthorization',
  REVISION_TO_POC = 'revision-to-poc',
  NEW_ACUTE_PERIOD = 'new-acute-period'
}

export interface AssessmentStatusState {
  inQA: boolean
  inQAEdit: boolean
  status: string | null
}

interface AssessmentContextProps {
  hasRoleToEdit: boolean
  questionnaire: BaseQuestionnaireStructureFragment
  children: React.ReactNode
  assessmentId: string
  assessmentType: AssessmentType
  assessmentStatusState: AssessmentStatusState
  initialSavedResponse: QuestionnaireResponse
  initialFlatAnswers: Record<string, QuestionnaireResponseItem>
  flowStage: Task | null
}

export interface AssessmentContextType {
  flow: AcutePeriodFlow | null // Determines if the assessment is for a new or existing acute period
  assessmentId: string | null
  assessmentType: AssessmentType | null // Type of assessment (e.g. 'SOC', 'PAT', 'Recert', etc)
  assessmentStatusState: AssessmentStatusState
  assessmentMustBeFinalized: boolean
  cantEditAssessmentFields: boolean // Set of rules(check AssessmentContext.Provider rendering for more info) that determine if the assessment can be edited
  response: QuestionnaireResponse | null // This will hold the entire QuestionnaireResponse state
  setResponse: React.Dispatch<React.SetStateAction<QuestionnaireResponse | null>> // Function to update the response
  answers: Record<string, QuestionnaireResponseItemAnswer> // This will hold the questionnaire answers
  setResponseItems: (newResponseItems: QuestionnaireResponseItem[]) => void // Function that grabs each new item and updates both response and answers accordingly
  flatAnswers: Record<string, QuestionnaireResponseItem> // Structured as linkId: answer for constant-time update
  updateFlatAnswers: (...linkIdsWithAnswers: LinkIdWithItem[]) => void // Updates the flatAnswers structure
  flowStage: Task | null // Stage of a flow where this assessment is being used
  questionnaire: BaseQuestionnaireStructureFragment
  isTestingAssessmentVersion: boolean
  isOutdatedAssessment: boolean
}

const AssessmentContext = createContext<AssessmentContextType>({
  flow: null,
  assessmentId: null,
  assessmentType: null,
  assessmentStatusState: {
    inQA: false,
    inQAEdit: false,
    status: null
  },
  assessmentMustBeFinalized: true,
  cantEditAssessmentFields: false,
  response: null,
  setResponse: () => {},
  answers: {},
  setResponseItems: () => {},
  flatAnswers: {},
  updateFlatAnswers: () => {},
  flowStage: null,
  questionnaire: {} as BaseQuestionnaireStructureFragment,
  isTestingAssessmentVersion: false,
  isOutdatedAssessment: false
})

export const useAssessmentContext = (): AssessmentContextType => {
  return useContext(AssessmentContext)
}

export const AssessmentContextProvider: FC<AssessmentContextProps> = ({
  hasRoleToEdit,
  questionnaire,
  children,
  assessmentId,
  assessmentType,
  assessmentStatusState,
  initialSavedResponse,
  initialFlatAnswers,
  flowStage
}) => {
  const medplum = useMedplum()

  const { patientId } = useContext(PatientContext)
  const { idToPatientMapping } = useStore((state) => state.patient)
  const { byState: healthcareServicesByState } = useStore((state) => state.serviceType)
  const { canReadWrite } = useContext(AccessPolicyContext)
  const { canWrite } = canReadWrite('QuestionnaireResponse')

  const { debugLogs } = useFeatureFlags()

  const { data: latestVersion } = useQuery({
    queryKey: [QueryKeys.ASSESSMENT_LATEST_VERSION, assessmentType],
    queryFn: async (): Promise<string> => {
      const response = await new HttpClient().fetch(
        `${BASE_API_URL}/assessments/versions/${assessmentType}`
      )
      if (!response.ok) {
        throw new Error(`Failed to fetch latest assessment version: ${response.status}`)
      }
      const result = (await response.json()) as { latest_version: string }
      return result.latest_version
    }
  })

  const isOutdatedAssessment = useMemo(() => {
    if (!latestVersion) return false
    return questionnaire.version !== latestVersion
  }, [questionnaire.version, latestVersion])

  const cantEditAssessmentFields =
    !canWrite ||
    assessmentStatusState.inQA ||
    !hasRoleToEdit ||
    COMPLETION_STATUSES.includes(assessmentStatusState.status as AssessmentStatus)

  const patientHealthcareServices = useMemo(() => {
    const patient = patientId ? idToPatientMapping[patientId] : undefined
    const patientState = patient?.addressState ?? undefined
    return patientState ? healthcareServicesByState[patientState] ?? [] : []
  }, [healthcareServicesByState, idToPatientMapping, patientId])

  const { data: priorAuthRequests } = useSuspenseQuery({
    queryKey: [QueryKeys.SERVICE_REQUESTS, patientId, OrderType.PRIOR_AUTH_REQUEST],
    queryFn: async (): Promise<ServiceRequest[]> => {
      const serviceRequests = await medplum.searchResources('ServiceRequest', {
        patient: `Patient/${patientId}`,
        code: LOINC_CODE_PRIOR_AUTH_REQUEST
      })
      return Array.isArray(serviceRequests) ? serviceRequests : []
    }
  })

  // When response is updated it will trigger an autosave in the Assessment specific controller (i.e. PATAssessmentController / SOCAssessmentController)
  const [response, setResponse] = useState<QuestionnaireResponse | null>(initialSavedResponse)

  const [flatAnswers, setFlatAnswers] =
    useState<Record<SocAssessmentQuestionnaireItem, QuestionnaireResponseItem>>(initialFlatAnswers)

  const answers = useMemo(() => {
    return removeNullValues(extractAnswers(response ?? undefined)) as Record<
      string,
      QuestionnaireResponseItemAnswer
    >
  }, [response])

  const setResponseItems = useCallback(
    (newResponseItems: QuestionnaireResponseItem[]): void => {
      if (cantEditAssessmentFields) return

      setResponse((prevResponse: QuestionnaireResponse) => {
        const updatedResponse = newResponseItems.reduce(
          (acc, item) => {
            if (acc.item && typeof item.linkId === 'string') {
              const updatedItem = updateObjectByLinkId(acc.item, item.linkId, item)
              return { ...acc, item: updatedItem }
            }
            return acc
          },
          {
            ...prevResponse,
            resourceType: 'QuestionnaireResponse' as const // to workaround the janky fhir type system
          }
        )

        return updatedResponse
      })
    },
    [cantEditAssessmentFields]
  )

  // SOC version
  const updateFlatAnswers = useCallback(
    (...linkIdsWithItems: LinkIdWithItem[]) => {
      if (cantEditAssessmentFields || linkIdsWithItems.length === 0) return

      if (debugLogs) {
        console.warn(
          '[AssessmentContext] Updating flat answers with link IDs with items',
          linkIdsWithItems
        )
      }

      const valuesByLinkId = Object.fromEntries(
        linkIdsWithItems.map(({ linkId, item }) => [linkId, item])
      )

      setFlatAnswers((prevFlatAnswers) => {
        return { ...prevFlatAnswers, ...valuesByLinkId }
      })
      setResponse((prevResponse) => {
        if (!prevResponse) {
          return response
        }

        const nestedAnswers = rebuildResponseFromFlat(flatAnswers)
        return { ...prevResponse, item: nestedAnswers }
      })
    },
    [cantEditAssessmentFields, debugLogs, flatAnswers, response]
  )

  const assessmentMustBeFinalized = useMemo(() => {
    if (assessmentType === AssessmentType.SERVICE_PLAN) return true

    const [authorizationSource] = getAnswerValuesFromItems(
      Object.values(flatAnswers),
      SocAssessmentQuestionnaireItem.AUTHORIZATION_AUTHORIZATION_AUTHORIZATION_SOURCE
    ) as (Reference | null)[]

    const authorizationSourceReference = authorizationSource?.reference
    if (!authorizationSourceReference) return true

    const matchingPriorAuthFromSelection = priorAuthRequests?.find(
      (priorAuthRequest) => `ServiceRequest/${priorAuthRequest.id}` === authorizationSourceReference
    )

    // FUA: only MA-HHA / TN-HHA prior auth requests tighten required fields; skilled PA types stay optional (ENG-5610).
    if (assessmentType === AssessmentType.FUA) {
      const isPriorAuthServiceRequest =
        matchingPriorAuthFromSelection &&
        matchingPriorAuthFromSelection.resourceType === 'ServiceRequest' &&
        matchingPriorAuthFromSelection.code?.coding?.some(
          (coding) => coding.code === LOINC_CODE_PRIOR_AUTH_REQUEST
        )

      if (isPriorAuthServiceRequest) {
        const primaryServiceTypeName = getPriorAuthRequestPrimaryServiceTypeName(
          matchingPriorAuthFromSelection,
          patientHealthcareServices
        )
        return (
          primaryServiceTypeName === ServiceTypeName.MA_HHA ||
          primaryServiceTypeName === ServiceTypeName.TN_HHA
        )
      }

      return true
    }

    return getShouldFinalizeAssessment({
      assessmentType,
      authorizationSource: matchingPriorAuthFromSelection
    })
  }, [assessmentType, flatAnswers, patientHealthcareServices, priorAuthRequests])

  // Determine the acutePeriodFlow based on the current flow
  const [reasonForDocumentation] = getAnswerValuesFromItems(
    Object.values(flatAnswers),
    FuaAssessmentQuestionnaireItem.ASSESSMENT_OVERVIEW_REASON_FOR_DOCUMENTATION
  )

  const flow = useMemo(() => {
    if (assessmentType !== AssessmentType.FUA) return null
    return getFlowFromReasonForDocumentation(reasonForDocumentation)
  }, [assessmentType, reasonForDocumentation])

  useEffect(() => {
    if (debugLogs) {
      console.warn('[AssessmentContext] Assessment context is reloading...')
    }
  }, [debugLogs])

  const isTestingAssessmentVersion = useMemo(() => {
    return questionnaire.status === QuestionnaireStatus.DRAFT
  }, [questionnaire.status])

  return (
    <AssessmentContext.Provider
      value={{
        flow,
        assessmentId,
        assessmentType,
        assessmentStatusState,
        assessmentMustBeFinalized,
        cantEditAssessmentFields,
        response,
        setResponse,
        answers,
        setResponseItems,
        flatAnswers,
        updateFlatAnswers,
        flowStage,
        questionnaire,
        isTestingAssessmentVersion,
        isOutdatedAssessment
      }}
    >
      {children}
    </AssessmentContext.Provider>
  )
}

export default AssessmentContext
